import React, {memo} from 'react';
import parse from 'html-react-parser';
import Container from '@/components/atoms/Container';
import ArrowButton from '@/components/atoms/ArrowButton';
import SectionHeader from '@/components/molecules/SectionHeader';
import {colors, fontWeight} from '@/styles/tokens';
import {useAutoAdvance} from '@/hooks/useAutoAdvance';
import styles from './HomeProcessWheel.module.scss';

// ─── Donut geometry ──────────────────────────────────────────────────────────
const VIEWBOX = 400;
const CENTER = VIEWBOX / 2;
const OUTER_R = 190;
const INNER_R = 128;
const LABEL_R = (OUTER_R + INNER_R) / 2;
// Gap between segments, in degrees.
const SEGMENT_GAP = 2;

// Segment fills, cycled in order — the process wheel's own palette, clockwise from
// the first step. Not in tokens: these five exist only for this wheel.
const SEGMENT_COLORS = [colors.primary.link, '#1C5DD6', colors.text.muted, colors.primary.midnight, colors.primary.deep];

// ─── Auto-advance timer ──────────────────────────────────────────────────────
// How long each step holds the detail panel before the wheel moves on. Keep in sync with
// the sweep animation duration in the module ($step-ms).
const STEP_MS = 3500;
// The travelling line sits inside the donut hole, just clear of the ring's inner edge.
const PROGRESS_R = INNER_R - 10;

const polar = (angleDeg: number, radius: number) => {
    const rad = ((angleDeg - 90) * Math.PI) / 180;
    return {x: CENTER + radius * Math.cos(rad), y: CENTER + radius * Math.sin(rad)};
};

/** Filled ring segment between two angles. */
const segmentPath = (startAngle: number, endAngle: number) => {
    const outerStart = polar(startAngle, OUTER_R);
    const outerEnd = polar(endAngle, OUTER_R);
    const innerEnd = polar(endAngle, INNER_R);
    const innerStart = polar(startAngle, INNER_R);
    const largeArc = endAngle - startAngle > 180 ? 1 : 0;

    return [
        `M ${outerStart.x} ${outerStart.y}`,
        `A ${OUTER_R} ${OUTER_R} 0 ${largeArc} 1 ${outerEnd.x} ${outerEnd.y}`,
        `L ${innerEnd.x} ${innerEnd.y}`,
        `A ${INNER_R} ${INNER_R} 0 ${largeArc} 0 ${innerStart.x} ${innerStart.y}`,
        'Z'
    ].join(' ');
};

/** Open arc the label text runs along. Text on the bottom half of a clockwise arc
 *  renders upside down, so that arc is drawn counter-clockwise instead. */
const labelPath = (startAngle: number, endAngle: number) => {
    const mid = (startAngle + endAngle) / 2;
    const flip = mid > 90 && mid < 270;
    const [from, to] = flip ? [endAngle, startAngle] : [startAngle, endAngle];
    const start = polar(from, LABEL_R);
    const end = polar(to, LABEL_R);

    return `M ${start.x} ${start.y} A ${LABEL_R} ${LABEL_R} 0 0 ${flip ? 0 : 1} ${end.x} ${end.y}`;
};

/** Open arc the timer line is drawn along, matching the active segment's sweep. */
const progressPath = (startAngle: number, endAngle: number) => {
    const start = polar(startAngle, PROGRESS_R);
    const end = polar(endAngle, PROGRESS_R);
    const largeArc = endAngle - startAngle > 180 ? 1 : 0;

    return `M ${start.x} ${start.y} A ${PROGRESS_R} ${PROGRESS_R} 0 ${largeArc} 1 ${end.x} ${end.y}`;
};

interface ProcessStep {
    stepNumber: number;
    category: string;
    heading: string;
    description: string;
    deliverables?: string[];
    duration?: string;
}

interface HomeProcessWheelProps {
    eyebrow?: string;
    heading: string;
    subheading?: string;
    steps: ProcessStep[];
    centreTitle?: string;
    centreSubtitle?: string;
    ctaText?: string;
    ctaUrl?: string;
}

const HomeProcessWheel = ({
    eyebrow,
    heading,
    subheading,
    steps,
    centreTitle = 'Engineering',
    centreSubtitle = 'Process',
    ctaText = 'Discuss Your Project Requirements',
    ctaUrl = '/contact-us'
}: HomeProcessWheelProps) => {
    const {activeIndex, select, cycle, armed, paused, containerProps} = useAutoAdvance({
        count: steps.length,
        intervalMs: STEP_MS
    });
    const activeStep = steps[activeIndex] ?? steps[0];
    const sweep = 360 / steps.length;

    return (
        <Container>
            <SectionHeader eyebrow={eyebrow} heading={heading} subheading={subheading ? parse(subheading) : ''} />

            <div className={styles.grid}>
                <div className={`${styles.item} ${styles.wheelCol}`}>
                    <div className={styles.wheelWrap} {...containerProps}>
                        <svg viewBox={`0 0 ${VIEWBOX} ${VIEWBOX}`} className={styles.svg} aria-hidden="true">
                            <defs>
                                {steps.map((step, index) => (
                                    <path
                                        key={step.stepNumber}
                                        id={`process-label-${step.stepNumber}`}
                                        d={labelPath(index * sweep + SEGMENT_GAP, (index + 1) * sweep - SEGMENT_GAP)}
                                        fill="none"
                                    />
                                ))}
                            </defs>

                            {steps.map((step, index) => (
                                <g key={step.stepNumber}>
                                    <path
                                        role="button"
                                        d={segmentPath(index * sweep + SEGMENT_GAP, (index + 1) * sweep - SEGMENT_GAP)}
                                        fill={SEGMENT_COLORS[index % SEGMENT_COLORS.length]}
                                        onMouseEnter={() => select(index)}
                                        onFocus={() => select(index)}
                                        onClick={() => select(index)}
                                    />
                                    <text
                                        fill={colors.text.inverse}
                                        fontSize="15"
                                        fontWeight={fontWeight.bold}
                                        textAnchor="middle"
                                        dominantBaseline="middle"
                                        className="font-headers"
                                    >
                                        <textPath href={`#process-label-${step.stepNumber}`} startOffset="50%">
                                            {step.category}
                                        </textPath>
                                    </text>
                                </g>
                            ))}

                            <circle cx={CENTER} cy={CENTER} r={INNER_R - 4} fill={colors.background.default} />

                            {/* The sweep: steps already passed stay drawn in their own
                                segment's colour, and the active one draws itself over
                                STEP_MS. Because "drawn" means `index < activeIndex`, the
                                ring clears itself the moment the cycle wraps back to 0. */}
                            {armed && (
                                <>
                                    {steps.slice(0, activeIndex).map((step, index) => (
                                        <path
                                            key={step.stepNumber}
                                            d={progressPath(index * sweep + SEGMENT_GAP, (index + 1) * sweep - SEGMENT_GAP)}
                                            stroke={SEGMENT_COLORS[index % SEGMENT_COLORS.length]}
                                            className={styles.progressArc}
                                        />
                                    ))}
                                    <path
                                        // Re-keyed on every advance so the CSS sweep
                                        // restarts in step with the countdown.
                                        key={cycle}
                                        d={progressPath(activeIndex * sweep + SEGMENT_GAP, (activeIndex + 1) * sweep - SEGMENT_GAP)}
                                        pathLength={1}
                                        stroke={SEGMENT_COLORS[activeIndex % SEGMENT_COLORS.length]}
                                        className={`${styles.progressArc} ${styles.progressLine}`}
                                        style={{animationPlayState: paused ? 'paused' : 'running'}}
                                    />
                                </>
                            )}
                        </svg>

                        <div className={styles.centreLabel}>
                            <p className={`font-headers ${styles.centreTitle}`}>{centreTitle}</p>
                            <p className={`font-regular-text ${styles.centreSubtitle}`}>{centreSubtitle}</p>
                            {activeStep?.duration && <p className={`font-headers ${styles.centreDuration}`}>{activeStep.duration}</p>}
                        </div>
                    </div>

                    {/* Accessible, keyboard-operable equivalent of the SVG segments. */}
                    <div className={styles.srOnly}>
                        {steps.map((step, index) => (
                            <button key={step.stepNumber} type="button" aria-pressed={activeIndex === index} onClick={() => select(index)}>
                                {step.category}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Every step's copy is rendered so it all sits in the server HTML for
                    crawlers; only the active one is shown. */}
                <div className={`${styles.item} ${styles.detailCol}`}>
                    {steps.map((step, index) => (
                        <div key={step.stepNumber} className={`${styles.detailCard} ${activeIndex === index ? '' : styles.hidden}`}>
                            <div className={styles.stepHeadRow}>
                                <span className={`font-headers ${styles.stepNumber}`}>{step.stepNumber}</span>
                                <div>
                                    <p className={`font-headers ${styles.category}`}>{step.category}</p>
                                    <h3 className={`font-headers ${styles.stepHeading}`}>{step.heading}</h3>
                                </div>
                            </div>

                            <p className={`font-regular-text ${styles.desc}`}>{step.description}</p>

                            {step.deliverables && step.deliverables.length > 0 && (
                                <>
                                    <p className={`font-headers ${styles.deliverablesLabel}`}>Deliverables</p>
                                    <div className={styles.tagsWrap}>
                                        {step.deliverables.map((item) => (
                                            <span key={item} className={`font-headers ${styles.tag}`}>
                                                {item}
                                            </span>
                                        ))}
                                    </div>
                                </>
                            )}

                            {step.duration && (
                                <div className={`font-headers ${styles.durationRow}`}>
                                    <span aria-hidden="true">⏱</span>
                                    {step.duration}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>

            <div className={styles.ctaWrap}>
                <ArrowButton variant="primary" href={ctaUrl} target="_blank">
                    {ctaText}
                </ArrowButton>
            </div>
        </Container>
    );
};

export default memo(HomeProcessWheel);
